import React, { useState, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import * as d3 from 'd3';
import SocketService from '../../../../packages/shared/socket';
import { useScreenNavigation } from '../../../../packages/shared/useScreenNavigation';
import { useScreenStateUpdates } from '../../../../packages/shared/useScreenStateUpdates';
import ScreenHeader from '../components/ScreenHeader';
import ScreenNavigation from '../components/ScreenNavigation';
import KuutioLoadingIndicator from '../components/KuutioLoadingIndicator';
import { useTranslation } from '../hooks/useTranslation';

const ScreenContainer = styled.div`
  width: 100vw;
  height: 100vh;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
  overflow: hidden;
  position: relative;
`;

const Header = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  padding: 20px;
  background: rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(10px);
  z-index: 500;
`;

const Stats = styled.div`
  position: absolute;
  top: 80px;
  left: 20px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  padding: 15px;
  border-radius: 10px;
  color: white;
  font-size: 0.9rem;
  z-index: 100;
  max-width: 250px;
`;

const ClusterInfo = styled.div`
  position: absolute;
  top: 80px;
  right: 20px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  padding: 15px;
  border-radius: 10px;
  color: white;
  font-size: 0.9rem;
  z-index: 100;
  max-width: 300px;
  max-height: 70vh;
  overflow-y: auto;
`;

const ClusterItem = styled.div`
  margin: 10px 0;
  padding: 10px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  border-left: 3px solid ${props => props.color};
`;

const SVGContainer = styled.svg`
  width: 100%;
  height: 100%;
`;

const ControlPanel = styled.div`
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  padding: 15px 30px;
  border-radius: 30px;
  z-index: 100;
  display: flex;
  gap: 15px;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-wrap: wrap;
    padding: 12px 20px;
    gap: 10px;
  }
`;

const ToggleButton = styled.button`
  padding: 8px 20px;
  background: ${props => props.active ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.1)'};
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 20px;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

// LoadingIndicator now imported from KuutioLoadingIndicator component

// Impact Machine specific styled components
const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(10px);
  display: ${props => props.visible ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
  border-radius: 20px;
  padding: 30px;
  width: 90vw;
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.5);
`;

const ImpactPanel = styled.div`
  position: absolute;
  top: 50%;
  right: 20px;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(10px);
  padding: 20px;
  border-radius: 15px;
  color: white;
  font-size: 0.9rem;
  z-index: 200;
  width: 350px;
  max-height: 60vh;
  overflow-y: auto;
  display: ${props => props.visible ? 'block' : 'none'};
`;

const ImpactHeader = styled.div`
  font-weight: bold;
  font-size: 1.1rem;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ConsequenceInput = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 10px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  color: white;
  font-size: 0.9rem;
  resize: vertical;
  margin-bottom: 10px;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
`;

const CommentInput = styled.input`
  width: 100%;
  padding: 8px 10px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  color: white;
  font-size: 0.9rem;
  margin-bottom: 10px;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
`;

const LikeButtons = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 15px;
`;

const LikeButton = styled.button`
  flex: 1;
  padding: 8px 15px;
  background: ${props => props.active ? props.color : 'rgba(255, 255, 255, 0.1)'};
  border: 1px solid ${props => props.active ? props.color : 'rgba(255, 255, 255, 0.3)'};
  border-radius: 8px;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9rem;
  
  &:hover {
    background: ${props => props.color};
    opacity: 0.8;
  }
`;

const ImpactList = styled.div`
  margin-top: 20px;
`;

const ImpactItem = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 10px;
  border-left: 3px solid ${props => props.color};
`;

const BubbleDetails = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 15px;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  padding-bottom: 15px;
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.3s ease;
  
  &:hover {
    opacity: 1;
  }
`;

const ActionTabs = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 25px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const ActionTab = styled.button`
  padding: 10px 20px;
  background: ${props => props.active ? 'rgba(255, 255, 255, 0.2)' : 'transparent'};
  border: none;
  border-bottom: 2px solid ${props => props.active ? '#60A5FA' : 'transparent'};
  color: white;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const ActionSection = styled.div`
  display: ${props => props.visible ? 'block' : 'none'};
  margin-bottom: 20px;
`;

const SubmitButton = styled.button`
  padding: 12px 24px;
  background: linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%);
  border: none;
  border-radius: 8px;
  color: white;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(59, 130, 246, 0.4);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const QuickActions = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 20px;
`;

const QuickActionButton = styled.button`
  flex: 1;
  padding: 15px;
  background: ${props => props.active ? props.color : 'rgba(255, 255, 255, 0.1)'};
  border: 1px solid ${props => props.active ? props.color : 'rgba(255, 255, 255, 0.3)'};
  border-radius: 12px;
  color: white;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 500;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  
  &:hover {
    background: ${props => props.color};
    transform: scale(1.05);
  }
`;

const CommentCounter = styled.div`
  position: absolute;
  top: -8px;
  right: -8px;
  background: #3B82F6;
  color: white;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: bold;
  border: 2px solid white;
`;

const ToggleGroup = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 15px;
`;

const ToggleOption = styled.button`
  flex: 1;
  padding: 10px 15px;
  background: ${props => props.active ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.1)'};
  border: 1px solid ${props => props.active ? 'rgba(255, 255, 255, 0.4)' : 'rgba(255, 255, 255, 0.2)'};
  border-radius: 8px;
  color: white;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
`;

const BubbleSelector = styled.select`
  width: 100%;
  padding: 10px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  color: white;
  font-size: 0.9rem;
  margin-bottom: 10px;
  
  option {
    background: #1e293b;
    color: white;
  }
`;

// What the wall says instead of bubbles: loading, no saved plan, or what failed (#1413)
const StatusMessage = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  max-width: 600px;
  padding: 24px 32px;
  background: rgba(0, 0, 0, 0.4);
  border-radius: 12px;
  color: white;
  font-size: 1.2rem;
  text-align: center;
  z-index: 50;
`;

// The saved plan's answer is awaited this long before the wall says it never came
const LOAD_ANSWER_TIMEOUT_MS = 15000;

// Configuration variables for bubble visualization
const BUBBLE_CONFIG = {
  BUBBLE_RADIUS: 45,
  CLUSTER_RADIUS_BASE: 120,
  CLUSTER_RADIUS_PER_ITEM: 8,
  MAX_TEXT_LENGTH: 35,
  FONT_SIZE: '0.8rem',
  LINE_HEIGHT: 1.1,
  MAX_LINES: 2,
  COLLISION_RADIUS: 50,
  FORCE_STRENGTH: 1.2,
  CHARGE_STRENGTH: -80,
  SNAP_RADIUS: 150,
  STROKE_WIDTH_NORMAL: 2,
  STROKE_WIDTH_MULTI: 4,
  OPACITY: 0.9,
};

// Helper function to wrap text within a circle
function wrapTextInCircle(text, maxLength, maxLines) {
  if (!text) return [''];
  
  if (text.length <= maxLength) {
    return [text];
  }
  
  const words = text.split(' ');
  const lines = [];
  let currentLine = '';
  
  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    
    if (testLine.length > maxLength && currentLine) {
      lines.push(currentLine);
      currentLine = word;
      
      if (lines.length >= maxLines - 1) {
        const remaining = words.slice(words.indexOf(word));
        const lastLine = remaining.join(' ');
        if (lastLine.length > maxLength) {
          currentLine = lastLine.substring(0, maxLength - 3) + '...';
        } else {
          currentLine = lastLine;
        }
        break;
      }
    } else {
      currentLine = testLine;
    }
  }
  
  if (currentLine) {
    lines.push(currentLine);
  }
  
  return lines.slice(0, maxLines);
}

export function ImpactMachine({ screenMode = false }) {
  const svgRef = useRef();
  const [aiEnabled, setAiEnabled] = useState(true);
  const [comments, setComments] = useState([]);
  const [clusters, setClusters] = useState([]);
  const [relationships, setRelationships] = useState([]);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(false);
  const [simulation, setSimulation] = useState(null);
  const socketRef = useRef(null);
  
  // Add ref to track current state for saving
  const currentStateRef = useRef({
    comments: [],
    arrows: [],
    bubbleImpacts: {},
    clusters: []
  });

  // Impact Machine specific state
  const [selectedBubble, setSelectedBubble] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState('consequence'); // 'comment', 'consequence' - default to consequence
  const [newConsequence, setNewConsequence] = useState('');
  const [newComment, setNewComment] = useState('');
  const [consequenceType, setConsequenceType] = useState('new'); // 'new' or 'existing'
  const [selectedTargetBubble, setSelectedTargetBubble] = useState(null);
  const [bubbleImpacts, setBubbleImpacts] = useState({}); // Store consequences, comments, likes per bubble
  const [arrows, setArrows] = useState([]); // Store arrow connections between bubbles
  // 'loading' | 'loaded' | { key, reason } when loading the saved plan failed
  const [loadState, setLoadState] = useState('loading');
  const { t } = useTranslation();
  const [zoomTransform, setZoomTransform] = useState({ k: 1, x: 0, y: 0 }); // Track zoom/pan

  // Keep current state ref updated for closure-safe saving
  useEffect(() => {
    currentStateRef.current = {
      comments,
      arrows,
      bubbleImpacts,
      clusters,
      zoomTransform
    };
  }, [comments, arrows, bubbleImpacts, clusters, zoomTransform]);

  // Add screen navigation and state update hooks (disabled in screenMode - ScreenClient handles navigation)
  useScreenNavigation({ screenMode });
  
  // Create preview data for facilitator
  const previewData = {
    status: 'Active',
    bubbleCount: comments.length,
    battleGroups: clusters.length,
    consequences: comments.filter(c => c.isConsequence).length,
    arrows: arrows.length,
    avgSentiment: comments.length > 0 ? 
      (comments.reduce((sum, bubble) => {
        const impact = bubbleImpacts[bubble.id];
        return sum + ((impact?.likes || 0) - (impact?.dislikes || 0));
      }, 0) / comments.length).toFixed(1) : '0',
    mode: 'Strategic Planning'
  };

  useScreenStateUpdates('impact-machine', previewData);

  // Initialize socket connection and set up data loading/saving
  useEffect(() => {
    // Named so the cleanup removes only these from the shared socket (#1427)
    const onPlanLoaded = (data) => {
      console.log('📊 Strategic plan loaded from database:', data);
      
      // A failed load answers in the error envelope (#1487); {} means no plan is saved
      if (data?.success === false) {
        setLoadState({ key: 'impactMachine.loadFailed', reason: data.error?.message });
        return;
      }
      if (data.comments && data.comments.length > 0) {
        setComments(data.comments);
        console.log('📊 Loaded comments:', data.comments.length);
      }
      
      if (data.clusters) {
        setClusters(data.clusters);
        console.log('📊 Loaded clusters:', data.clusters.length);
      }
      
      if (data.arrows) {
        setArrows(data.arrows);
        console.log('📊 Loaded arrows:', data.arrows.length);
      }
      
      if (data.bubbleImpacts) {
        setBubbleImpacts(data.bubbleImpacts);
        console.log('📊 Loaded bubble impacts for', Object.keys(data.bubbleImpacts).length, 'bubbles');
      }
      
      setLoadState('loaded');
    };

    const onPlanSaved = () => console.log('✅ Strategic plan saved to database');

    const initSocket = async () => {
      try {
        console.log('🚀 Impact Machine initializing socket connection...');
        
        // Use SocketService
        const socket = SocketService.connect('impact-machine-session', 'display');
        socketRef.current = socket;
        console.log('✅ Impact Machine connected via SocketService');

        socket.on('strategic-plan-loaded', onPlanLoaded);
        socket.on('access-denied', onAccessDenied);
        socket.on('strategic-plan-saved', onPlanSaved);

        // Request load of existing data after a small delay to ensure backend is ready
        setTimeout(() => {
          console.log('📤 Requesting load of strategic plan...');
          socket.emit('load-strategic-plan', { sessionType: 'impact-machine' });
        }, 500);

      } catch (error) {
        console.error('❌ Impact Machine socket connection failed:', error);
        setLoadState({ key: 'impactMachine.connectFailed', reason: error.message });
      }
    };

    // The backend refuses load-strategic-plan without a session
    const onAccessDenied = ({ event } = {}) => {
      if (event === 'load-strategic-plan') setLoadState({ key: 'impactMachine.accessDenied' });
    };

    initSocket();

    // No answer at all is a failure too, not a wall waiting forever
    const answerTimer = setTimeout(() => {
      setLoadState((s) => (s === 'loading' ? { key: 'impactMachine.noAnswer' } : s));
    }, LOAD_ANSWER_TIMEOUT_MS);

    return () => {
      console.log('🔌 Impact Machine cleaning up socket connection...');
      clearTimeout(answerTimer);
      if (socketRef.current) {
        socketRef.current.off('strategic-plan-loaded', onPlanLoaded);
        socketRef.current.off('strategic-plan-saved', onPlanSaved);
        socketRef.current.off('access-denied', onAccessDenied);
      }
    };
  }, []);

  // Handle bubble click to show modal
  const handleBubbleClick = (bubble) => {
    console.log('🎯 handleBubbleClick called with:', bubble);
    setSelectedBubble(bubble);
    setShowModal(true);
    setActiveTab('consequence'); // Default to consequence tab
    setNewConsequence('');
    setNewComment('');
    console.log('✅ Modal should be visible now');
  };

  // Close modal
  const closeModal = () => {
    setShowModal(false);
    setSelectedBubble(null);
    setActiveTab('consequence');
    setNewConsequence('');
    setNewComment('');
    setConsequenceType('new');
    setSelectedTargetBubble(null);
  };

  // Handle bubble grouping when dragging bubbles together
  const handleBubbleGrouping = (bubble1, bubble2) => {
    console.log('🔗 Grouping bubbles:', bubble1.text, 'with', bubble2.text);
    
    // Find or create a cluster for these bubbles
    let targetCluster = null;
    
    // Check if either bubble already has a cluster
    if (bubble1.clusterId) {
      targetCluster = clusters.find(c => c.id === bubble1.clusterId);
    } else if (bubble2.clusterId) {
      targetCluster = clusters.find(c => c.id === bubble2.clusterId);
    }
    
    // If no existing cluster, create a new one
    if (!targetCluster) {
      const newClusterId = `cluster_${Date.now()}`;
      const clusterColors = ['#ff6b9d', '#45b7d1', '#96ceb4', '#feca57', '#ff6b6b', '#a55eea'];
      const newClusterColor = clusterColors[clusters.length % clusterColors.length];
      
      targetCluster = {
        id: newClusterId,
        name: `Battle ${clusters.length + 1}`,
        color: newClusterColor,
        commentIds: [],
        description: 'Strategic grouping'
      };
      
      setClusters(prev => [...prev, targetCluster]);
    }
    
    // Assign both bubbles to the target cluster
    setComments(prev => prev.map(comment => {
      if (comment.id === bubble1.id || comment.id === bubble2.id) {
        return {
          ...comment,
          clusterId: targetCluster.id,
          clusterColor: targetCluster.color
        };
      }
      return comment;
    }));
    
    console.log('✅ Bubbles grouped in cluster:', targetCluster.name);
  };

  // Add consequence to bubble - creates new bubble with arrow or links to existing
  const addConsequence = () => {
    console.log('🎯 addConsequence called');
    console.log('🎯 selectedBubble:', selectedBubble);
    console.log('🎯 consequenceType:', consequenceType);
    console.log('🎯 newConsequence:', newConsequence);
    console.log('🎯 selectedTargetBubble:', selectedTargetBubble);
    
    if (!selectedBubble) {
      console.log('❌ No selected bubble');
      return;
    }
    
    if (consequenceType === 'new' && newConsequence.trim()) {
      console.log('✅ Creating new consequence bubble');
      // Create new consequence bubble
      const newBubbleId = `consequence_${Date.now()}`;
      const newBubble = {
        id: newBubbleId,
        text: newConsequence.trim(),
        author: 'User',
        timestamp: new Date().toISOString(),
        clusterId: null,
        clusterColor: '#8B5CF6', // Purple for consequences
        isConsequence: true,
        parentId: selectedBubble.id
      };
      
      console.log('📦 New bubble:', newBubble);
      
      // Add new bubble to comments
      setComments(prev => {
        const updated = [...prev, newBubble];
        console.log('📦 Updated comments:', updated.length);
        return updated;
      });
      
      // Initialize impact data for new bubble
      setBubbleImpacts(prev => {
        const updated = {
          ...prev,
          [newBubbleId]: {
            consequences: [],
            comments: [],
            likes: 0,
            dislikes: 0,
            userRating: null
          }
        };
        console.log('📦 Updated bubble impacts:', Object.keys(updated).length);
        return updated;
      });
      
      // Create arrow connection
      const newArrow = {
        id: `arrow_${Date.now()}`,
        sourceId: selectedBubble.id,
        targetId: newBubbleId,
        type: 'consequence'
      };
      
      console.log('🏹 New arrow:', newArrow);
      
      setArrows(prev => {
        const updated = [...prev, newArrow];
        console.log('🏹 Updated arrows:', updated.length);
        return updated;
      });
      
      setNewConsequence('');
      
      // Save after adding new consequence
      console.log('💾 Triggering save in 100ms...');
      setTimeout(() => {
        console.log('💾 Now calling saveToDatabase()');
        saveToDatabase();
      }, 100);
      
      closeModal();
      
    } else if (consequenceType === 'existing' && selectedTargetBubble) {
      console.log('✅ Linking to existing bubble:', selectedTargetBubble);
      // Link to existing bubble
      
      // Check if arrow already exists
      const existingArrow = arrows.find(
        a => a.sourceId === selectedBubble.id && a.targetId === selectedTargetBubble
      );
      
      if (!existingArrow) {
        const newArrow = {
          id: `arrow_${Date.now()}`,
          sourceId: selectedBubble.id,
          targetId: selectedTargetBubble,
          type: 'consequence'
        };
        console.log('🏹 Creating new arrow:', newArrow);
        
        setArrows(prev => {
          const updated = [...prev, newArrow];
          console.log('🏹 Updated arrows:', updated.length);
          return updated;
        });
        console.log('✅ Created arrow from', selectedBubble.text, 'to', 
                    comments.find(c => c.id === selectedTargetBubble)?.text);
      } else {
        console.log('⚠️ Arrow already exists');
      }
      
      // Save after adding consequence
      console.log('💾 Triggering save in 100ms...');
      setTimeout(() => {
        console.log('💾 Now calling saveToDatabase()');
        saveToDatabase();
      }, 100);
      
      closeModal();
    }
  };

  // Add comment to bubble
  const addComment = () => {
    if (!selectedBubble || !newComment.trim()) return;
    
    setBubbleImpacts(prev => ({
      ...prev,
      [selectedBubble.id]: {
        ...prev[selectedBubble.id],
        comments: [...(prev[selectedBubble.id]?.comments || []), {
          id: `comment_${Date.now()}`,
          text: newComment.trim(),
          timestamp: new Date().toISOString(),
          author: 'User'
        }]
      }
    }));
    
    // Save after adding comment
    setTimeout(() => saveToDatabase(), 100);
    
    setNewComment('');
  };

  // Handle like/dislike with visual size changes
  const handleRating = (type) => {
    if (!selectedBubble) return;
    
    console.log('👍👎 Rating clicked:', type, 'for bubble:', selectedBubble.text);
    
    setBubbleImpacts(prev => {
      const currentImpact = prev[selectedBubble.id] || {};
      const currentRating = currentImpact.userRating;
      
      let newLikes = currentImpact.likes || 0;
      let newDislikes = currentImpact.dislikes || 0;
      let newRating = type;
      
      // Handle rating change logic
      if (currentRating === type) {
        // Removing current rating
        newRating = null;
        if (type === 'like') newLikes--;
        if (type === 'dislike') newDislikes--;
      } else {
        // Changing or setting new rating
        if (currentRating === 'like') newLikes--;
        if (currentRating === 'dislike') newDislikes--;
        
        if (type === 'like') newLikes++;
        if (type === 'dislike') newDislikes++;
      }
      
      // Bubble size will be automatically updated by getBubbleSize() function
      const newState = {
        ...prev,
        [selectedBubble.id]: {
          ...currentImpact,
          likes: Math.max(0, newLikes),
          dislikes: Math.max(0, newDislikes),
          userRating: newRating
        }
      };
      
      console.log('👍👎 New bubble state:', newState[selectedBubble.id]);
      
      // Save immediately with the updated state
      setTimeout(() => {
        saveToDatabase(newState);
      }, 100);
      
      return newState;
    });
    
    closeModal();
  };

  // Get impact count for a bubble (for display on bubble)
  const getImpactCount = (bubbleId) => {
    const impact = bubbleImpacts[bubbleId];
    if (!impact) return 0;
    return (impact.consequences?.length || 0) + (impact.comments?.length || 0) + (impact.likes || 0) + (impact.dislikes || 0);
  };

  // Calculate bubble size based on sentiment
  const getBubbleSize = (bubbleId) => {
    const impact = bubbleImpacts[bubbleId];
    if (!impact) return 1;
    
    const likes = impact.likes || 0;
    const dislikes = impact.dislikes || 0;
    const netSentiment = likes - dislikes;
    
    // Base size multiplier between 0.6 (very negative) and 1.6 (very positive)
    // More likes = bigger bubble, more dislikes = smaller bubble
    const sentimentMultiplier = Math.max(0.6, Math.min(1.6, 1 + (netSentiment * 0.15)));
    
    return sentimentMultiplier;
  };

  // Get comment count for a bubble
  const getCommentCount = (bubbleId) => {
    const impact = bubbleImpacts[bubbleId];
    return impact?.comments?.length || 0;
  };


  // Handle adding comments
  const handleAddComment = () => {
    console.log('💬 handleAddComment called, newComment:', newComment);
    if (!selectedBubble || !newComment.trim()) return;
    
    const comment = {
      id: `comment_${Date.now()}`,
      text: newComment.trim(),
      author: 'Current User', // Could be made dynamic
      timestamp: new Date().toISOString()
    };
    
    console.log('💬 Adding comment to bubble:', selectedBubble.id, comment);
    
    setBubbleImpacts(prev => {
      const current = prev[selectedBubble.id] || { consequences: [], comments: [], likes: 0, dislikes: 0, userRating: null };
      const updated = {
        ...prev,
        [selectedBubble.id]: {
          ...current,
          comments: [...(current.comments || []), comment]
        }
      };
      console.log('💬 Updated bubble impacts:', updated[selectedBubble.id]);
      console.log('💬 Will trigger save with updated impacts');
      // Trigger save with the updated state
      setTimeout(() => {
        saveToDatabase(updated);
      }, 100);
      return updated;
    });
    
    setNewComment('');
    console.log('💬 Added comment to', selectedBubble.text, ':', comment);
  };

  // Debug modal state
  useEffect(() => {
    console.log('🔍 Modal state changed:', { showModal, selectedBubble: selectedBubble?.id });
  }, [showModal, selectedBubble]);

  // Track zoom transform

  // Load data from API using the socket event approach that was already set up
  const loadDataFromAPI = async () => {
    // This will be called by the socket event handler when data is loaded
    console.log('📊 Waiting for data from socket connection...');
  };

  // Save strategic planning data over the socket (save-strategic-plan)
  const saveToDatabase = async (overrideBubbleImpacts = null) => {
    console.log('💾 saveToDatabase called with override:', overrideBubbleImpacts ? 'YES' : 'NO');
    console.log('💾 socketRef.current exists?', !!socketRef.current);
    console.log('💾 socketRef.current connected?', socketRef.current?.connected);
    console.log('💾 SocketService connected?', SocketService.isConnected());
    
    // Use ref to get current state values to avoid closure issues
    const currentState = currentStateRef.current;
    const currentBubbleImpacts = overrideBubbleImpacts || currentState.bubbleImpacts;
    const currentComments = currentState.comments;
    const currentClusters = currentState.clusters;
    const currentArrows = currentState.arrows;
    const currentZoomTransform = currentState.zoomTransform;
    
    console.log('💾 Current state from ref:', {
      comments: currentComments.length,
      arrows: currentArrows.length,
      bubbleImpacts: Object.keys(currentBubbleImpacts).length
    });
    
    // Capture bubble positions with their fixed positions
    const bubblePositions = {};
    currentComments.forEach(comment => {
      bubblePositions[comment.id] = {
        x: comment.x || 0,
        y: comment.y || 0,
        fixedX: comment.fixedX || null,
        fixedY: comment.fixedY || null
      };
    });
    
    // Enhanced data structure with comprehensive tracking
    const planningData = {
      comments: currentComments,
      clusters: currentClusters,
      arrows: currentArrows,
      bubbleImpacts: currentBubbleImpacts, // This includes comments, likes, dislikes for each bubble
      bubblePositions,
      zoomTransform: currentZoomTransform,
      timestamp: new Date().toISOString(),
      sessionType: 'impact-machine',
      // Additional metadata for tracking
      stats: {
        totalBubbles: currentComments.length,
        totalArrows: currentArrows.length,
        totalComments: Object.values(currentBubbleImpacts).reduce((sum, impact) => sum + (impact.comments?.length || 0), 0),
        totalLikes: Object.values(currentBubbleImpacts).reduce((sum, impact) => sum + (impact.likes || 0), 0),
        totalDislikes: Object.values(currentBubbleImpacts).reduce((sum, impact) => sum + (impact.dislikes || 0), 0),
        consequenceBubbles: currentComments.filter(c => c.isConsequence).length
      }
    };

    try {
      // Try using SocketService directly if socketRef is not available
      const socket = socketRef.current || SocketService.socket;
      
      if (!socket) {
        console.error('❌ No socket connection available');
        return;
      }
      
      if (!socket.connected) {
        console.error('❌ Socket exists but not connected');
        return;
      }
      
      console.log('🚀 Emitting save-strategic-plan event...');
      socket.emit('save-strategic-plan', planningData);
      console.log('💾 Saved complete impact machine state:', {
        bubbles: planningData.stats.totalBubbles,
        arrows: planningData.stats.totalArrows,
        comments: planningData.stats.totalComments,
        likes: planningData.stats.totalLikes,
        dislikes: planningData.stats.totalDislikes,
        consequences: planningData.stats.consequenceBubbles
      });
    } catch (error) {
      console.error('❌ Failed to save strategic planning data:', error);
    }
  };

  // Manual save only - no auto-save
  // useEffect(() => {
  //   const saveInterval = setInterval(saveToDatabase, 30000);
  //   return () => clearInterval(saveInterval);
  // }, [comments, clusters, arrows, bubbleImpacts, zoomTransform]);

  // Helper function to get bubble color
  const getBubbleColor = (bubble) => {
    if (bubble.text.includes('SITUATION:')) return '#FF6B6B'; // Red for situation
    if (bubble.text.includes('GOAL:')) return '#10B981'; // Green for goal
    if (bubble.isConsequence) return '#8B5CF6'; // Purple for consequences
    return bubble.clusterColor || '#60A5FA'; // Default blue for actions
  };

  // Helper function to calculate depth level for left-to-right flow
  const getDepthLevel = (bubbleId, allComments, allArrows) => {
    // Find if this bubble is a consequence of another
    const parentArrow = allArrows.find(arrow => arrow.targetId === bubbleId);
    if (!parentArrow) {
      // This is a root node (situation or original idea)
      return 0;
    }
    // Recursively find the depth
    return 1 + getDepthLevel(parentArrow.sourceId, allComments, allArrows);
  };

  // D3 Visualization (simplified version focused on impact vectors)
  useEffect(() => {
    if (!comments.length) return;

    const svg = d3.select(svgRef.current);
    const width = window.innerWidth;
    const height = window.innerHeight;

    svg.selectAll('*').remove();
    const container = svg.append('g');

    // Add zoom with state tracking
    const zoom = d3.zoom()
      .scaleExtent([0.5, 3])
      .on('zoom', (event) => {
        container.attr('transform', event.transform);
        // Track zoom state for saving
        setZoomTransform({
          x: event.transform.x,
          y: event.transform.y,
          k: event.transform.k
        });
      });
    svg.call(zoom);
    
    // Restore saved zoom state
    if (zoomTransform && (zoomTransform.x !== 0 || zoomTransform.y !== 0 || zoomTransform.k !== 1)) {
      const transform = d3.zoomIdentity
        .translate(zoomTransform.x, zoomTransform.y)
        .scale(zoomTransform.k);
      svg.call(zoom.transform, transform);
      console.log('🔍 Restored zoom state:', zoomTransform);
    }

    // Strategic flow layout: Situation → Actions → Goal
    const bubblePositions = {};
    
    // Categorize bubbles by type
    const situationBubbles = comments.filter(c => c.text.includes('SITUATION:'));
    const goalBubbles = comments.filter(c => c.text.includes('GOAL:'));
    const actionBubbles = comments.filter(c => !c.text.includes('SITUATION:') && !c.text.includes('GOAL:') && !c.isConsequence);
    const consequenceBubbles = comments.filter(c => c.isConsequence);
    
    // Position SITUATION bubbles on the left
    situationBubbles.forEach((bubble, index) => {
      bubblePositions[bubble.id] = {
        x: 150,
        y: height/2 + (index - situationBubbles.length/2) * 100
      };
    });
    
    // Position GOAL bubbles on the right
    goalBubbles.forEach((bubble, index) => {
      bubblePositions[bubble.id] = {
        x: width - 150,
        y: height/2 + (index - goalBubbles.length/2) * 100
      };
    });
    
    // Position ACTION bubbles in the middle
    actionBubbles.forEach((bubble, index) => {
      const columns = Math.ceil(actionBubbles.length / 3); // Max 3 rows
      const col = Math.floor(index / 3);
      const row = index % 3;
      
      bubblePositions[bubble.id] = {
        x: width/2 + (col - columns/2) * 200,
        y: height/2 + (row - 1) * 120
      };
    });
    
    // Position CONSEQUENCE bubbles to the right of their parents
    consequenceBubbles.forEach((bubble, index) => {
      const parentBubble = comments.find(c => c.id === bubble.parentId);
      const parentPos = bubblePositions[bubble.parentId];
      
      if (parentPos) {
        bubblePositions[bubble.id] = {
          x: parentPos.x + 250,
          y: parentPos.y + (index % 2 === 0 ? -50 : 50)
        };
      } else {
        // Fallback position
        bubblePositions[bubble.id] = {
          x: width - 300,
          y: height/2 + index * 80
        };
      }
    });

    // Add strategic flow indicators
    const flowGroup = container.append('g').attr('class', 'flow-indicators');
    
    // Flow direction arrow from situation to goal
    flowGroup.append('line')
      .attr('x1', 250)
      .attr('y1', height - 100)
      .attr('x2', width - 250)
      .attr('y2', height - 100)
      .attr('stroke', 'rgba(255, 255, 255, 0.3)')
      .attr('stroke-width', 2)
      .attr('stroke-dasharray', '5,5')
      .attr('marker-end', 'url(#flow-arrow)');
    
    // Add flow labels
    flowGroup.append('text')
      .attr('x', 150)
      .attr('y', height - 120)
      .attr('text-anchor', 'middle')
      .attr('fill', 'rgba(255, 255, 255, 0.7)')
      .attr('font-size', '0.9rem')
      .attr('font-weight', 'bold')
      .text('🎯 CURRENT STATE');
      
    flowGroup.append('text')
      .attr('x', width/2)
      .attr('y', height - 120)
      .attr('text-anchor', 'middle')
      .attr('fill', 'rgba(255, 255, 255, 0.7)')
      .attr('font-size', '0.9rem')
      .attr('font-weight', 'bold')
      .text('⚔️ MUST-WIN BATTLES');
      
    flowGroup.append('text')
      .attr('x', width - 150)
      .attr('y', height - 120)
      .attr('text-anchor', 'middle')
      .attr('fill', 'rgba(255, 255, 255, 0.7)')
      .attr('font-size', '0.9rem')
      .attr('font-weight', 'bold')
      .text('🏆 TARGET VISION');

    // Draw arrows first (so they appear below bubbles) - will be updated dynamically
    const arrowGroup = container.append('g').attr('class', 'arrows');
    
    // Add arrow marker definitions
    const defs = container.append('defs');
    
    // Regular consequence arrows
    defs.append('marker')
      .attr('id', 'arrowhead')
      .attr('viewBox', '0 0 10 10')
      .attr('refX', 9)
      .attr('refY', 3)
      .attr('markerWidth', 6)
      .attr('markerHeight', 6)
      .attr('orient', 'auto')
      .append('path')
      .attr('d', 'M0,0 L0,6 L9,3 z')
      .attr('fill', '#60A5FA');
      
    // Flow direction arrows  
    defs.append('marker')
      .attr('id', 'flow-arrow')
      .attr('viewBox', '0 0 10 10')
      .attr('refX', 9)
      .attr('refY', 3)
      .attr('markerWidth', 8)
      .attr('markerHeight', 8)
      .attr('orient', 'auto')
      .append('path')
      .attr('d', 'M0,0 L0,6 L9,3 z')
      .attr('fill', 'rgba(255, 255, 255, 0.3)');

    // Create force simulation with very weak forces to allow manual positioning
    const newSimulation = d3.forceSimulation(comments)
      .force('charge', d3.forceManyBody().strength(-30)) // Very weak repulsion
      .force('collision', d3.forceCollide().radius(d => getBubbleSize(d.id) * BUBBLE_CONFIG.BUBBLE_RADIUS + 5))
      .force('x', d3.forceX(d => d.fixedX || bubblePositions[d.id]?.x || width/2).strength(0.05)) // Very weak centering
      .force('y', d3.forceY(d => d.fixedY || bubblePositions[d.id]?.y || height/2).strength(0.05)); // Very weak centering

    // Create comment nodes with drag functionality
    const commentGroups = container.append('g')
      .attr('class', 'comments')
      .selectAll('g')
      .data(comments)
      .enter()
      .append('g')
      .attr('class', 'comment-group')
      .style('cursor', 'pointer')
      .on('click', function(event, d) {
        // Only trigger click if not dragging
        if (!d3.select(this).classed('dragging')) {
          console.log('🖱️ Bubble clicked:', d);
          event.stopPropagation();
          handleBubbleClick(d);
        }
      })
      .call(d3.drag()
        .on('start', function(event, d) {
          if (!event.active) newSimulation.alphaTarget(0.3).restart();
          d.fx = d.x;
          d.fy = d.y;
          d3.select(this).classed('dragging', true);
          console.log('🖱️ Started dragging:', d.text);
        })
        .on('drag', function(event, d) {
          d.fx = event.x;
          d.fy = event.y;
          
          // Visual feedback during drag
          d3.select(this).select('circle')
            .attr('stroke', '#60A5FA')
            .attr('stroke-width', 4);
        })
        .on('end', function(event, d) {
          if (!event.active) newSimulation.alphaTarget(0);
          
          // Check for grouping/clustering opportunities
          let nearestBubble = null;
          let minDistance = Infinity;
          const snapRadius = 80; // Distance to snap to another bubble
          
          comments.forEach((otherComment) => {
            if (otherComment.id !== d.id && otherComment.x && otherComment.y) {
              const distance = Math.sqrt(
                Math.pow(event.x - otherComment.x, 2) + 
                Math.pow(event.y - otherComment.y, 2)
              );
              
              if (distance < minDistance && distance < snapRadius) {
                minDistance = distance;
                nearestBubble = otherComment;
              }
            }
          });
          
          if (nearestBubble) {
            console.log('🔗 Grouping bubbles:', d.text, 'with', nearestBubble.text);
            // Create or join cluster
            handleBubbleGrouping(d, nearestBubble);
          }
          
          // Reset visual styling
          d3.select(this).select('circle')
            .attr('stroke', d => {
              const impactCount = getImpactCount(d.id);
              if (impactCount > 5) return '#FFD700';
              if (impactCount > 2) return '#FFA500';
              return 'white';
            })
            .attr('stroke-width', d => {
              const impactCount = getImpactCount(d.id);
              return Math.min(6, 2 + impactCount * 0.5);
            });
          
          d3.select(this).classed('dragging', false);
          
          // Keep the bubble in its new position with weak force
          d.fixedX = event.x;
          d.fixedY = event.y;
          d.fx = null;
          d.fy = null;
        }));

    // Add circles with dynamic sizing based on sentiment
    commentGroups.append('circle')
      .attr('r', d => getBubbleSize(d.id) * BUBBLE_CONFIG.BUBBLE_RADIUS)
      .attr('fill', d => {
        if (d.text.includes('SITUATION:')) return '#FF6B6B'; // Red for situation
        if (d.text.includes('GOAL:')) return '#10B981'; // Green for goal
        if (d.isConsequence) return '#8B5CF6'; // Purple for consequences
        return d.clusterColor || '#60A5FA'; // Default blue for actions
      })
      .attr('opacity', BUBBLE_CONFIG.OPACITY)
      .attr('stroke', d => {
        const impactCount = getImpactCount(d.id);
        if (impactCount > 5) return '#FFD700'; // Gold for high impact
        if (impactCount > 2) return '#FFA500'; // Orange for medium impact
        return 'white'; // White for low/no impact
      })
      .attr('stroke-width', d => {
        const impactCount = getImpactCount(d.id);
        return Math.min(6, 2 + impactCount * 0.5); // Thicker stroke for more impact
      });

    // Remove the separate impact indicator - we'll use bubble size instead

    // Add comment counter in top-right corner
    commentGroups.filter(d => getCommentCount(d.id) > 0)
      .append('g')
      .attr('class', 'comment-counter')
      .each(function(d) {
        const group = d3.select(this);
        const commentCount = getCommentCount(d.id);
        const radius = getBubbleSize(d.id) * BUBBLE_CONFIG.BUBBLE_RADIUS;
        
        // Position in top-right corner of bubble
        const cx = radius * 0.7;
        const cy = -radius * 0.7;
        
        // Background circle
        group.append('circle')
          .attr('cx', cx)
          .attr('cy', cy)
          .attr('r', 12)
          .attr('fill', '#3B82F6')
          .attr('stroke', 'white')
          .attr('stroke-width', 2);
        
        // Comment count text
        group.append('text')
          .attr('x', cx)
          .attr('y', cy + 1)
          .attr('text-anchor', 'middle')
          .attr('fill', 'white')
          .attr('font-size', '0.7rem')
          .attr('font-weight', 'bold')
          .attr('pointer-events', 'none')
          .style('pointer-events', 'none')
          .text(commentCount);
      });

    // Add impact count text (different position)
    commentGroups.append('text')
      .attr('x', 0)
      .attr('y', d => getBubbleSize(d.id) * BUBBLE_CONFIG.BUBBLE_RADIUS + 20)
      .attr('text-anchor', 'middle')
      .attr('fill', 'white')
      .attr('font-size', '0.7rem')
      .attr('font-weight', 'bold')
      .attr('pointer-events', 'none')
      .style('pointer-events', 'none')
      .text(d => {
        const impactCount = getImpactCount(d.id);
        return impactCount > 0 ? `⚡${impactCount}` : '';
      });

    // Add text with proper line wrapping
    commentGroups.each(function(d) {
      const group = d3.select(this);
      const textLines = wrapTextInCircle(d.text, BUBBLE_CONFIG.MAX_TEXT_LENGTH, BUBBLE_CONFIG.MAX_LINES);
      
      const lineCount = textLines.length;
      const totalHeight = (lineCount - 1) * parseFloat(BUBBLE_CONFIG.FONT_SIZE.replace('rem', '')) * 16 * BUBBLE_CONFIG.LINE_HEIGHT;
      const startY = -totalHeight / 2;
      
      textLines.forEach((line, i) => {
        group.append('text')
          .attr('text-anchor', 'middle')
          .attr('fill', 'white')
          .attr('font-size', BUBBLE_CONFIG.FONT_SIZE)
          .attr('font-weight', '500')
          .attr('pointer-events', 'none')
          .style('pointer-events', 'none')
          .attr('y', startY + (i * parseFloat(BUBBLE_CONFIG.FONT_SIZE.replace('rem', '')) * 16 * BUBBLE_CONFIG.LINE_HEIGHT))
          .text(line);
      });
    });

    // Add circular consequence button to each bubble (bottom-right)
    commentGroups.each(function(d) {
      const group = d3.select(this);
      const bubbleRadius = getBubbleSize(d.id) * BUBBLE_CONFIG.BUBBLE_RADIUS;
      
      // Position button at bottom-right of bubble
      const buttonX = bubbleRadius * 0.7;
      const buttonY = bubbleRadius * 0.7;
      const buttonRadius = 12;
      
      // Create button group
      const buttonGroup = group.append('g')
        .attr('class', 'consequence-button')
        .attr('transform', `translate(${buttonX}, ${buttonY})`)
        .style('cursor', 'pointer');
      
      // Background circle
      buttonGroup.append('circle')
        .attr('r', buttonRadius)
        .attr('fill', 'rgba(0, 0, 0, 0.8)')
        .attr('stroke', getBubbleColor(d))
        .attr('stroke-width', 2);
      
      // Icon
      buttonGroup.append('text')
        .attr('text-anchor', 'middle')
        .attr('dy', '0.35em')
        .attr('fill', 'white')
        .attr('font-size', '14px')
        .attr('font-weight', 'bold')
        .attr('pointer-events', 'none')
        .text('⚡');
      
      // Drag functionality for linking bubbles
      let isDragging = false;
      let dragLine = null;
      let dragStartTime = 0;
      
      buttonGroup.call(d3.drag()
        .on('start', function(event, d) {
          console.log('🎯 Drag start event triggered for:', d.text);
          if (event.sourceEvent) event.sourceEvent.stopPropagation();
          isDragging = true;
          dragStartTime = Date.now();
          
          // Create drag line
          dragLine = container.append('line')
            .attr('stroke', getBubbleColor(d))
            .attr('stroke-width', 3)
            .attr('stroke-dasharray', '5,5')
            .attr('opacity', 0.8)
            .attr('x1', d.x + buttonX)
            .attr('y1', d.y + buttonY)
            .attr('x2', d.x + buttonX)
            .attr('y2', d.y + buttonY);
          
          console.log('🎯 Started dragging consequence from:', d.text);
        })
        .on('drag', function(event, d) {
          if (!isDragging) return;
          
          // Update drag line
          const sourceX = d.x + buttonX;
          const sourceY = d.y + buttonY;
          
          // Get the mouse position accounting for zoom transformation
          const transform = d3.zoomTransform(container.node());
          const mouseX = (event.sourceEvent.clientX - transform.x) / transform.k;
          const mouseY = (event.sourceEvent.clientY - transform.y) / transform.k;
          
          // Alternative method using SVG coordinate transformation
          const svgElement = container.node().ownerSVGElement || container.node();
          const pt = svgElement.createSVGPoint();
          pt.x = event.sourceEvent.clientX;
          pt.y = event.sourceEvent.clientY;
          
          // Get the container's transform matrix
          const containerNode = container.node();
          const ctm = containerNode.getCTM ? containerNode.getCTM() : containerNode.getScreenCTM();
          const transformedPt = pt.matrixTransform(ctm.inverse());
          
          dragLine
            .attr('x1', sourceX)
            .attr('y1', sourceY)
            .attr('x2', transformedPt.x)
            .attr('y2', transformedPt.y);
        })
        .on('end', function(event, d) {
          if (!isDragging) return;
          isDragging = false;
          
          console.log('🎯 Drag ended at:', event.x, event.y);
          console.log('🎯 Source event:', event.sourceEvent);
          
          // Remove drag line
          if (dragLine) {
            dragLine.remove();
            dragLine = null;
          }
          
          // Find target bubble under cursor - try multiple methods
          let targetData = null;
          
          // Method 1: Use document.elementFromPoint
          if (event.sourceEvent && event.sourceEvent.clientX && event.sourceEvent.clientY) {
            const targetElement = document.elementFromPoint(event.sourceEvent.clientX, event.sourceEvent.clientY);
            console.log('🎯 Element at cursor:', targetElement);
            
            if (targetElement) {
              const targetBubble = d3.select(targetElement.closest('.comment-group'));
              if (!targetBubble.empty()) {
                targetData = targetBubble.datum();
                console.log('🎯 Found target via elementFromPoint:', targetData?.text);
              }
            }
          }
          
          // Method 2: Check all bubbles for collision
          if (!targetData) {
            // Get the mouse position accounting for zoom transformation
            const svgElement = container.node().ownerSVGElement || container.node();
            const pt = svgElement.createSVGPoint();
            pt.x = event.sourceEvent.clientX;
            pt.y = event.sourceEvent.clientY;
            
            // Get the container's transform matrix (includes zoom)
            const containerNode = container.node();
            const ctm = containerNode.getCTM ? containerNode.getCTM() : containerNode.getScreenCTM();
            const transformedPt = pt.matrixTransform(ctm.inverse());
            
            const dragX = transformedPt.x;
            const dragY = transformedPt.y;
            
            comments.forEach(comment => {
              if (comment.id === d.id) return; // Skip self
              
              const distance = Math.sqrt(
                Math.pow(comment.x - dragX, 2) + Math.pow(comment.y - dragY, 2)
              );
              
              const bubbleRadius = getBubbleSize(comment.id) * BUBBLE_CONFIG.BUBBLE_RADIUS;
              
              if (distance <= bubbleRadius + 20) { // 20px tolerance
                targetData = comment;
                console.log('🎯 Found target via collision:', targetData.text);
              }
            });
          }
          
          if (targetData && targetData.id !== d.id) {
            // Create arrow connection
            const newArrow = {
              id: `arrow_${Date.now()}`,
              sourceId: d.id,
              targetId: targetData.id,
              type: 'consequence'
            };
            
            // Check if arrow already exists
            const existingArrow = arrows.find(
              a => a.sourceId === d.id && a.targetId === targetData.id
            );
            
            if (!existingArrow) {
              setArrows(prev => [...prev, newArrow]);
              console.log('✅ Created drag-link from', d.text, 'to', targetData.text);
              
              // Save after creating arrow via drag
              setTimeout(() => saveToDatabase(), 100);
            } else {
              console.log('⚠️ Arrow already exists');
            }
          } else {
            console.log('⚠️ No valid target found for drag');
          }
          
          // Handle tap vs drag - if it was a short duration without much movement, treat as tap
          const dragDuration = Date.now() - dragStartTime;
          if (dragDuration < 200 && !targetData) {
            console.log('🔮 Treating as tap - opening modal');
            setSelectedBubble(d);
            setActiveTab('consequence');
            setShowModal(true);
          }
        })
      );
    });

    // Function to update arrows dynamically with delete buttons
    const updateArrows = () => {
      // Update arrow lines
      const arrowLines = arrowGroup.selectAll('.arrow-line')
        .data(arrows, d => d.id);
      
      // Remove old arrows
      arrowLines.exit().remove();
      
      // Add new arrow groups
      const newArrowGroups = arrowLines.enter()
        .append('g')
        .attr('class', 'arrow-line');
      
      // Add the line
      newArrowGroups.append('line')
        .attr('stroke', '#60A5FA')
        .attr('stroke-width', 3)
        .attr('opacity', 0.7)
        .attr('marker-end', 'url(#arrowhead)')
        .style('cursor', 'pointer');
      
      // Add invisible wider line for easier clicking
      newArrowGroups.append('line')
        .attr('stroke', 'transparent')
        .attr('stroke-width', 20)
        .style('cursor', 'pointer')
        .on('click', function(event, d) {
          event.stopPropagation();
          // Show delete button on this arrow
          d3.select(this.parentNode).select('.delete-button').style('display', 'block');
        });
      
      // Add delete button group (initially hidden)
      const deleteButtonGroup = newArrowGroups.append('g')
        .attr('class', 'delete-button')
        .style('display', 'none')
        .style('cursor', 'pointer');
      
      deleteButtonGroup.append('circle')
        .attr('r', 10)
        .attr('fill', 'rgba(239, 68, 68, 0.9)')
        .attr('stroke', 'white')
        .attr('stroke-width', 2);
      
      deleteButtonGroup.append('text')
        .attr('text-anchor', 'middle')
        .attr('dy', '0.35em')
        .attr('fill', 'white')
        .attr('font-size', '14px')
        .attr('font-weight', 'bold')
        .attr('pointer-events', 'none')
        .text('×');
      
      deleteButtonGroup.on('click', function(event, d) {
        event.stopPropagation();
        setArrows(prev => prev.filter(a => a.id !== d.id));
        console.log('🗑️ Deleted arrow:', d.id);
      });
      
      // Update all arrows (new and existing)
      arrowGroup.selectAll('.arrow-line').each(function(d) {
        const group = d3.select(this);
        const sourceNode = comments.find(c => c.id === d.sourceId);
        const targetNode = comments.find(c => c.id === d.targetId);
        
        if (sourceNode && targetNode) {
          // Calculate line from center to center
          const dx = targetNode.x - sourceNode.x;
          const dy = targetNode.y - sourceNode.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          // Calculate where line intersects bubble edges
          const sourceRadius = getBubbleSize(sourceNode.id) * BUBBLE_CONFIG.BUBBLE_RADIUS;
          const targetRadius = getBubbleSize(targetNode.id) * BUBBLE_CONFIG.BUBBLE_RADIUS;
          
          // Start from center, end at edge
          const x1 = sourceNode.x;
          const y1 = sourceNode.y;
          const x2 = targetNode.x - (dx / distance) * targetRadius;
          const y2 = targetNode.y - (dy / distance) * targetRadius;
          
          // Update visible line
          group.select('line:nth-child(1)')
            .attr('x1', x1)
            .attr('y1', y1)
            .attr('x2', x2)
            .attr('y2', y2);
          
          // Update invisible click area
          group.select('line:nth-child(2)')
            .attr('x1', x1)
            .attr('y1', y1)
            .attr('x2', x2)
            .attr('y2', y2);
          
          // Position delete button at midpoint
          group.select('.delete-button')
            .attr('transform', `translate(${(x1 + x2) / 2}, ${(y1 + y2) / 2})`);
        }
      });
      
      // Hide delete buttons when clicking elsewhere
      container.on('click', function() {
        arrowGroup.selectAll('.delete-button').style('display', 'none');
      });
    };

    // Update positions and arrows
    newSimulation.on('tick', () => {
      commentGroups.attr('transform', d => `translate(${d.x}, ${d.y})`);
      updateArrows();
    });
    
    // Initial arrow draw
    updateArrows();

    setSimulation(newSimulation);

    return () => {
      if (newSimulation) newSimulation.stop();
    };
  }, [comments, clusters, bubbleImpacts, arrows]);

  return (
    <ScreenContainer>
      <ScreenHeader 
        title={<ScreenNavigation currentScreenTitle="Impact Machine" />}
        subtitle="Strategic planning with consequences"
        rightContent={
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <button style={{ 
              padding: '6px 12px', 
              background: 'rgba(76, 175, 80, 0.2)', 
              border: '1px solid #4CAF50',
              borderRadius: '4px',
              color: '#4CAF50',
              fontSize: '0.9rem',
              cursor: 'pointer'
            }}>
              Save Plan
            </button>
            <div style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.8rem' }}>
              {comments.length} ideas • {clusters.length} clusters
            </div>
          </div>
        }
      />
      
      <Stats>
        <div>💬 Ideas: {comments.length}</div>
        <div>🏷️ Clusters: {clusters.length}</div>
        <div>⚡ Total Impact: {Object.values(bubbleImpacts).reduce((sum, impact) => {
          return sum + (impact.consequences?.length || 0) + (impact.comments?.length || 0) + (impact.likes || 0) + (impact.dislikes || 0);
        }, 0)}</div>
        <div>👍 Positive: {Object.values(bubbleImpacts).reduce((sum, impact) => sum + (impact.likes || 0), 0)}</div>
        <div>👎 Negative: {Object.values(bubbleImpacts).reduce((sum, impact) => sum + (impact.dislikes || 0), 0)}</div>
        <div style={{ fontSize: '0.8rem', opacity: 0.8, marginTop: '10px' }}>
          💡 Click any bubble to add consequences, comments, or rate it
        </div>
      </Stats>
      
      <ClusterInfo>
        <strong>⚔️ Strategic Battle Groups:</strong>
        {clusters.map(cluster => {
          const clusterBubbles = comments.filter(c => c.clusterId === cluster.id);
          return (
            <ClusterItem key={cluster.id} color={cluster.color}>
              <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>{cluster.name}</div>
              <div style={{ fontSize: '0.8rem', opacity: 0.9 }}>{cluster.description}</div>
              <div style={{ fontSize: '0.7rem', marginTop: '5px' }}>
                {clusterBubbles.length} actions | 
                Avg Sentiment: {clusterBubbles.length > 0 ? 
                  (clusterBubbles.reduce((sum, bubble) => {
                    const impact = bubbleImpacts[bubble.id];
                    return sum + ((impact?.likes || 0) - (impact?.dislikes || 0));
                  }, 0) / clusterBubbles.length).toFixed(1) : '0'}
              </div>
            </ClusterItem>
          );
        })}
        {clusters.length === 0 && (
          <div style={{ fontSize: '0.8rem', opacity: 0.7, fontStyle: 'italic' }}>
            Drag bubbles together to create strategic battle groups
          </div>
        )}
      </ClusterInfo>
      
      <ControlPanel>
        <div style={{ color: 'white', fontSize: '0.9rem' }}>
          ⚡ Impact Mode Active - Click bubbles to interact
        </div>
        <div style={{ color: 'white', fontSize: '0.8rem', opacity: 0.8 }}>
          💬 Comments: {Object.values(bubbleImpacts).reduce((sum, impact) => sum + (impact.comments?.length || 0), 0)} | 
          🔮 Consequences: {comments.filter(c => c.isConsequence).length} | 
          ➡️ Arrows: {arrows.length}
        </div>
      </ControlPanel>

      <Modal visible={showModal && selectedBubble} onClick={closeModal}>
        {selectedBubble && (
          <ModalContent 
            onClick={(e) => e.stopPropagation()}
            style={{
              borderTop: `4px solid ${getBubbleColor(selectedBubble)}`
            }}
          >
            <ModalHeader>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
                <div>
                  <ModalTitle style={{ marginBottom: '4px' }}>
                    ⚡ {selectedBubble.text}
                  </ModalTitle>
                  <div style={{ fontSize: '0.8rem', opacity: 0.7 }}>
                    by {selectedBubble.author} • {new Date(selectedBubble.timestamp).toLocaleDateString()}
                  </div>
                </div>
                <CloseButton onClick={closeModal} style={{ alignSelf: 'flex-start' }}>✕</CloseButton>
              </div>
            </ModalHeader>

            <QuickActions>
              <QuickActionButton
                color="#10B981"
                active={bubbleImpacts[selectedBubble.id]?.userRating === 'like'}
                onClick={() => handleRating('like')}
              >
                👍 Like ({bubbleImpacts[selectedBubble.id]?.likes || 0})
              </QuickActionButton>
              <QuickActionButton
                color="#EF4444"
                active={bubbleImpacts[selectedBubble.id]?.userRating === 'dislike'}
                onClick={() => handleRating('dislike')}
              >
                👎 Dislike ({bubbleImpacts[selectedBubble.id]?.dislikes || 0})
              </QuickActionButton>
            </QuickActions>

            <ActionTabs>
              <ActionTab 
                active={activeTab === 'consequence'} 
                onClick={() => setActiveTab('consequence')}
              >
                🔮 Consequence {(() => {
                  const outgoingCount = arrows.filter(arrow => arrow.sourceId === selectedBubble.id).length;
                  const incomingCount = arrows.filter(arrow => arrow.targetId === selectedBubble.id).length;
                  const totalCount = outgoingCount + incomingCount;
                  return totalCount > 0 ? `(${totalCount})` : '';
                })()}
              </ActionTab>
              <ActionTab 
                active={activeTab === 'comment'} 
                onClick={() => setActiveTab('comment')}
              >
                💬 Comment {bubbleImpacts[selectedBubble.id]?.comments?.length > 0 ? 
                  `(${bubbleImpacts[selectedBubble.id].comments.length})` : ''
                }
              </ActionTab>
            </ActionTabs>

            <ActionSection visible={activeTab === 'comment'}>
              <CommentInput
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Add your thoughts or feedback..."
                onKeyPress={(e) => e.key === 'Enter' && e.target.value.trim() && (handleAddComment(), setNewComment(''))}
              />
              <SubmitButton 
                onClick={() => { handleAddComment(); setNewComment(''); }} 
                disabled={!newComment.trim()}
              >
                Add Comment
              </SubmitButton>
            </ActionSection>

            <ActionSection visible={activeTab === 'consequence'}>
              <div style={{ marginBottom: '15px' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '10px' }}>🔮 Add Consequence:</div>
                
                <ConsequenceInput
                  value={newConsequence}
                  onChange={(e) => setNewConsequence(e.target.value)}
                  placeholder="What could happen if this idea is implemented? This will create a new connected bubble..."
                />
                
                <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                  <SubmitButton 
                    onClick={addConsequence} 
                    disabled={!newConsequence.trim()}
                    style={{ flex: 1 }}
                  >
                    Create Consequence Bubble
                  </SubmitButton>
                  
                  <div style={{ position: 'relative' }}>
                    <ToggleOption 
                      active={consequenceType === 'existing'} 
                      onClick={() => setConsequenceType('existing')}
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      🔗 Link to Existing ▼
                    </ToggleOption>
                    {consequenceType === 'existing' && (
                      <div 
                        ref={(el) => {
                          if (el) {
                            // Smart positioning: check if there's space below
                            const buttonRect = el.parentElement.getBoundingClientRect();
                            const spaceBelow = window.innerHeight - buttonRect.bottom;
                            const spaceAbove = buttonRect.top;
                            
                            if (spaceBelow < 250 && spaceAbove > 250) {
                              // Position above button
                              el.style.bottom = '100%';
                              el.style.top = 'auto';
                              el.style.marginTop = '0';
                              el.style.marginBottom = '5px';
                            } else {
                              // Position below button (default)
                              el.style.top = '100%';
                              el.style.bottom = 'auto';
                              el.style.marginTop = '5px';
                              el.style.marginBottom = '0';
                            }
                          }
                        }}
                        style={{
                          position: 'absolute',
                          left: 0,
                          right: 0,
                          minWidth: '280px',
                          background: 'rgba(0, 0, 0, 0.95)',
                          border: '1px solid rgba(255, 255, 255, 0.3)',
                          borderRadius: '8px',
                          zIndex: 10000,
                          maxHeight: '200px',
                          overflowY: 'auto',
                          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)'
                        }}
                      >
                        {comments
                          .filter(comment => comment.id !== selectedBubble?.id)
                          .map(comment => (
                            <div
                              key={comment.id}
                              onClick={() => {
                                setSelectedTargetBubble(comment.id);
                                setConsequenceType('new'); // Reset to close dropdown
                                setTimeout(() => addConsequence(), 100);
                              }}
                              style={{
                                padding: '10px 15px',
                                color: 'white',
                                cursor: 'pointer',
                                borderBottom: '1px solid rgba(255, 255, 255, 0.15)',
                                fontSize: '0.9rem',
                                transition: 'background 0.2s',
                                display: 'flex',
                                alignItems: 'center'
                              }}
                              onMouseOver={(e) => {
                                e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                              }}
                              onMouseOut={(e) => {
                                e.target.style.background = 'transparent';
                              }}
                            >
                              {comment.text.substring(0, 50)}{comment.text.length > 50 ? '...' : ''}
                            </div>
                          ))}
                        {comments.filter(comment => comment.id !== selectedBubble?.id).length === 0 && (
                          <div style={{ padding: '15px', color: 'rgba(255, 255, 255, 0.5)', textAlign: 'center' }}>
                            No other bubbles available
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Show existing arrow connections */}
                {(() => {
                  const outgoingArrows = arrows.filter(arrow => arrow.sourceId === selectedBubble.id);
                  const incomingArrows = arrows.filter(arrow => arrow.targetId === selectedBubble.id);
                  
                  return (outgoingArrows.length > 0 || incomingArrows.length > 0) && (
                    <div style={{ marginTop: '20px', padding: '15px', background: 'rgba(255, 255, 255, 0.05)', borderRadius: '8px' }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '10px', color: '#60A5FA' }}>➡️ Arrow Connections</div>
                      
                      {outgoingArrows.length > 0 && (
                        <div style={{ marginBottom: '15px' }}>
                          <div style={{ fontSize: '0.9rem', opacity: 0.8, marginBottom: '8px' }}>🔮 Consequences:</div>
                          {outgoingArrows.map(arrow => {
                            const targetBubble = comments.find(c => c.id === arrow.targetId);
                            return targetBubble && (
                              <div key={arrow.id} style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '8px 12px',
                                background: 'rgba(96, 165, 250, 0.1)',
                                borderRadius: '6px',
                                marginBottom: '5px',
                                fontSize: '0.85rem'
                              }}>
                                <span style={{ flex: 1 }}>
                                  {targetBubble.text.substring(0, 40)}{targetBubble.text.length > 40 ? '...' : ''}
                                </span>
                                <button
                                  onClick={() => {
                                    setArrows(prev => prev.filter(a => a.id !== arrow.id));
                                  }}
                                  style={{
                                    background: 'rgba(239, 68, 68, 0.2)',
                                    border: '1px solid rgba(239, 68, 68, 0.4)',
                                    borderRadius: '4px',
                                    color: '#EF4444',
                                    padding: '4px 8px',
                                    cursor: 'pointer',
                                    fontSize: '0.7rem',
                                    marginLeft: '10px'
                                  }}
                                >
                                  ✕
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      
                      {incomingArrows.length > 0 && (
                        <div>
                          <div style={{ fontSize: '0.9rem', opacity: 0.8, marginBottom: '8px' }}>⬅️ Parents:</div>
                          {incomingArrows.map(arrow => {
                            const sourceBubble = comments.find(c => c.id === arrow.sourceId);
                            return sourceBubble && (
                              <div key={arrow.id} style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '8px 12px',
                                background: 'rgba(34, 197, 94, 0.1)',
                                borderRadius: '6px',
                                marginBottom: '5px',
                                fontSize: '0.85rem'
                              }}>
                                <span style={{ flex: 1 }}>
                                  {sourceBubble.text.substring(0, 40)}{sourceBubble.text.length > 40 ? '...' : ''}
                                </span>
                                <button
                                  onClick={() => {
                                    setArrows(prev => prev.filter(a => a.id !== arrow.id));
                                  }}
                                  style={{
                                    background: 'rgba(239, 68, 68, 0.2)',
                                    border: '1px solid rgba(239, 68, 68, 0.4)',
                                    borderRadius: '4px',
                                    color: '#EF4444',
                                    padding: '4px 8px',
                                    cursor: 'pointer',
                                    fontSize: '0.7rem',
                                    marginLeft: '10px'
                                  }}
                                >
                                  ✕
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })()}
                
              </div>
            </ActionSection>

            {/* Show existing comments if any */}
            {bubbleImpacts[selectedBubble.id]?.comments?.length > 0 && (
              <div style={{ marginTop: '20px' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '10px' }}>💬 Comments ({bubbleImpacts[selectedBubble.id].comments.length}):</div>
                {bubbleImpacts[selectedBubble.id].comments.slice(-3).map(comment => (
                  <ImpactItem key={comment.id} color="#3B82F6">
                    <div style={{ fontSize: '0.9rem' }}>{comment.text}</div>
                    <div style={{ fontSize: '0.7rem', opacity: 0.7, marginTop: '5px' }}>
                      {new Date(comment.timestamp).toLocaleString()}
                    </div>
                  </ImpactItem>
                ))}
              </div>
            )}
          </ModalContent>
        )}
      </Modal>
      
      <KuutioLoadingIndicator 
        show={loading}
        text="Analyzing impact vectors..."
        subtitle="Mapping consequences and relationships"
      />

      {(loadState !== 'loaded' || comments.length === 0) && (
        <StatusMessage role={typeof loadState === 'object' ? 'alert' : 'status'}>
          {loadState === 'loading' && t('impactMachine.loading')}
          {loadState === 'loaded' && t('impactMachine.empty')}
          {typeof loadState === 'object' && t(loadState.key, { reason: loadState.reason })}
        </StatusMessage>
      )}

      <SVGContainer ref={svgRef} />
    </ScreenContainer>
  );
}

export default ImpactMachine;